import React, { useEffect, useState, ReactNode } from 'react';
import {
  Modal,
  Page,
  Button,
  Dropdown,
  DisplayText,
  Spinner
} from '@superops/ui-design-system';
import { FormattedMessage } from 'react-intl';
import { GET_ASSET_CLASS_LIST } from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import { RMMClient, AccountClient } from '@nile/graphql';
import { SVGIcon } from '@superops/super-icons';
import UnMonitorAssetUploader from '@nile/components/src/DromoUploader/Uploaders/UnMonitorAssetUploader';
import { GET_CUSTOMER_IDENTITY_TOKEN } from '@nile/components/src/DromoUploader/dromo';
import { useQuery } from '@apollo/client';
import { useActivityTracker } from '@nile/components';

interface AssetClass {
  classId: string;
  name: string;
  label: string;
  value: string;
}

interface UnmonitorAssetDromoUploadModalProps {
  activator: ReactNode;
  title?: string;
  userToken?: string;
  onResults?: () => void;
  onCancel?: () => void;
}

const UnmonitorAssetDromoUploadModal: React.FC<UnmonitorAssetDromoUploadModalProps> = ({
  activator,
  title = 'Import Asset',
  userToken: userTokenProp,
  onResults: onResultsProp,
  onCancel: onCancelProp
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [assetClassList, setAssetClassList] = useState<AssetClass[]>([]);
  const [
    selectedAssetClass,
    setSelectedAssetClass
  ] = useState<AssetClass | null>(null);
  const [nonAssetClassListLoading, setNonAssetClassListLoading] = useState<
    boolean
  >(false);

  const { data: token, loading: tokenLoading, error: tokenError } = useQuery(
    GET_CUSTOMER_IDENTITY_TOKEN,
    {
      fetchPolicy: 'network-only',
      client: AccountClient,
      skip: !!userTokenProp
    }
  );

  const userToken = userTokenProp || token?.getCustomerIdentityToken;

  const openModal = (): void => setIsOpen(true);
  const closeModal = (): void => {
    setIsOpen(false);
    setSelectedAssetClass(null);
  };

  const fetchNonMonitorAssetClassList = async (): Promise<void> => {
    setNonAssetClassListLoading(true);
    try {
      const { data: res } = await RMMClient.query({
        query: GET_ASSET_CLASS_LIST,
        fetchPolicy: 'network-only',
        variables: {
          moduleType: 'UN_MONITORED_ASSET'
        }
      });

      setAssetClassList(
        res?.getAssetClassList?.map((v: any) => ({
          ...v,
          label: v.name,
          value: v.classId
        })) || []
      );
    } catch (error) {
      console.error('Error fetching asset class list:', error);
    } finally {
      setNonAssetClassListLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchNonMonitorAssetClassList();
    }
  }, [isOpen]);

  const handleAssetClassChange = (value: AssetClass): void => {
    setSelectedAssetClass(value);
  };

  const handleImportComplete = () => {
    closeModal();
    useActivityTracker({
      event: 'unmonitored bulk asset upload'
    });
    if (onResultsProp) {
      onResultsProp();
    }
  };

  const handleCancel = () => {
    closeModal();
    if (onCancelProp) {
      onCancelProp();
    }
  };

  function getContinueBtn(isDisabled: boolean) {
    return (
      <Button
        disabled={isDisabled}
        primary
        onClick={() => {
          closeModal();
          useActivityTracker({
            event: 'Import unmonitored Asset initiated'
          });
        }}
        icon={<SVGIcon name="RightArrow" />}
        textAlign="right"
      >
        <FormattedMessage id="common.continue" />
      </Button>
    );
  }

  return (
    <>
      <div data-type="button" onClick={openModal}>
        {activator}
      </div>
      <Modal
        open={isOpen}
        onClose={closeModal}
        large
        limitHeight
        noScroll
        title=""
        showCloseButton={false}
      >
        <div className="" onClick={(e) => e.stopPropagation()}>
          <Page
            variant="primary"
            title={<div className="flex col g10">{title}</div>}
            primaryAction={
              <>
                {selectedAssetClass?.classId ? (
                  <div className="dromoBtnReset">
                    <UnMonitorAssetUploader
                      classId={selectedAssetClass?.classId}
                      userToken={userToken}
                      onCancel={handleCancel}
                      activator={getContinueBtn(false)}
                      onResults={handleImportComplete}
                    />
                  </div>
                ) : (
                  getContinueBtn(true)
                )}
              </>
            }
            secondaryActions={[
              {
                content: <FormattedMessage id="common.cancel" />,
                onAction: handleCancel
              }
            ]}
          >
            <div className="min-height-400">
              {nonAssetClassListLoading ? (
                <div className="flex-center mt-30">
                  <Spinner size="large" />
                </div>
              ) : (
                <div className="fx col g10">
                  <DisplayText
                    element="h3"
                    isRequired
                    size="large"
                    weight="semibold"
                  >
                    <FormattedMessage id="select.asset.type.upload.label" />
                  </DisplayText>
                  <Dropdown
                    options={assetClassList}
                    onChange={handleAssetClassChange}
                    value={selectedAssetClass}
                  />
                </div>
              )}
            </div>
          </Page>
        </div>
      </Modal>
    </>
  );
};

export default UnmonitorAssetDromoUploadModal;
